import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  ArrowLeft, Gauge, TrendUp, TrendDown, Minus, Clock, Sparkle,
} from "@phosphor-icons/react";

function ConfidenceDot({ score }) {
  const color = score >= 8 ? "#10b981" : score >= 5 ? "#f59e0b" : "#ef4444";
  return <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />;
}

function chipDiff(prev, curr) {
  const added = curr.filter((c) => !prev.includes(c));
  const removed = prev.filter((c) => !curr.includes(c));
  const kept = curr.filter((c) => prev.includes(c));
  return { added, removed, kept };
}

function DiffChips({ label, prev, curr }) {
  const { added, removed, kept } = chipDiff(prev || [], curr || []);
  if (!added.length && !removed.length && !kept.length) return null;
  return (
    <div className="space-y-1.5">
      <span className="text-[10px] font-medium text-[var(--text-muted)] uppercase tracking-wider">{label}</span>
      <div className="flex flex-wrap gap-1">
        {kept.map((c, i) => <span key={`k-${i}`} className="px-2 py-0.5 text-[10px] rounded-full bg-[var(--bg-hover)] text-[var(--text-secondary)] border border-[var(--border)]">{c}</span>)}
        {added.map((c, i) => <span key={`a-${i}`} className="px-2 py-0.5 text-[10px] rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">+ {c}</span>)}
        {removed.map((c, i) => <span key={`r-${i}`} className="px-2 py-0.5 text-[10px] rounded-full bg-red-500/10 text-red-400 border border-red-500/20 line-through">- {c}</span>)}
      </div>
    </div>
  );
}

function TrendArrow({ prev, curr }) {
  if (curr > prev) return <TrendUp size={14} className="text-emerald-400" />;
  if (curr < prev) return <TrendDown size={14} className="text-red-400" />;
  return <Minus size={14} className="text-[var(--text-muted)]" />;
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function VoiceHistoryPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const businessId = searchParams.get("business_id") || user?.active_business_id || "";

  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [current, setCurrent] = useState(null);
  const [limit, setLimit] = useState(15);

  useEffect(() => {
    const fetch = async () => {
      try {
        const resp = await api.get(`/brand/voice-history?business_id=${businessId}`);
        setHistory(resp.data.history || []);
        setCurrent(resp.data.current || null);
        setLimit(resp.data.limit || 15);
      } catch { /* silent */ }
      setLoading(false);
    };
    fetch();
  }, [api, businessId]);

  const timeline = [];
  if (current?.extracted_at) timeline.push({ ...current, _label: "Current" });
  for (let i = history.length - 1; i >= 0; i--) timeline.push({ ...history[i], _label: `Version ${i + 1}` });
  const totalEntries = timeline.length;

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <button onClick={() => navigate("/settings?tab=personalization")} className="flex items-center gap-1.5 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors mb-6" data-testid="back-to-settings">
          <ArrowLeft size={16} /> Back to Settings
        </button>

        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond']" data-testid="voice-history-title">Voice Evolution</h1>
            <p className="text-sm text-[var(--text-muted)] mt-1">Track how your brand voice has evolved over time</p>
          </div>
          <Button variant="outline" onClick={() => navigate(`/settings/personalization/voice-wizard?business_id=${businessId}`)} className="border-[var(--border)] text-[var(--text-secondary)] text-xs" data-testid="recapture-btn">
            <Sparkle size={14} className="mr-1.5" /> Re-capture Voice
          </Button>
        </div>

        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg px-4 py-2.5 mb-6 flex items-center gap-2" data-testid="history-limit-note">
          <Clock size={14} className="text-[var(--text-muted)] shrink-0" />
          <p className="text-xs text-[var(--text-muted)]">
            Showing {totalEntries} of {limit} max entries. History resets after {limit} voice captures — only the most recent {limit} are kept.
          </p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => <div key={i} className="h-40 bg-[var(--bg-card)] rounded-lg animate-pulse border border-[var(--border)]" />)}
          </div>
        ) : timeline.length === 0 ? (
          <div className="text-center py-20" data-testid="no-history">
            <Gauge size={48} className="mx-auto text-[var(--text-muted)] opacity-30 mb-4" />
            <h2 className="text-lg font-semibold text-[var(--text-primary)] mb-2">No voice history yet</h2>
            <p className="text-sm text-[var(--text-muted)] mb-6 max-w-md mx-auto">Complete the Voice Capture wizard at least once to start tracking your voice evolution.</p>
            <Button onClick={() => navigate(`/settings/personalization/voice-wizard?business_id=${businessId}`)} className="bg-[#FF4400] hover:bg-[#FF6633] text-white">
              <Sparkle size={16} className="mr-1.5" /> Capture Your Voice
            </Button>
          </div>
        ) : (
          <>
            {timeline.length >= 2 && (
              <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5 mb-6" data-testid="confidence-trend">
                <span className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-3 block">Confidence Trend</span>
                <div className="flex items-end gap-1 h-16">
                  {[...timeline].reverse().map((entry, i) => {
                    const pct = (entry.confidence_score || 0) * 10;
                    const color = entry.confidence_score >= 8 ? "#10b981" : entry.confidence_score >= 5 ? "#f59e0b" : "#ef4444";
                    return (
                      <div key={entry._label || `bar-${i}`} className="flex-1 flex flex-col items-center gap-1" title={`${entry._label}: ${entry.confidence_score}/10`}>
                        <span className="text-[9px] font-mono text-[var(--text-muted)]">{entry.confidence_score}</span>
                        <motion.div initial={{ height: 0 }} animate={{ height: `${pct}%` }} transition={{ duration: 0.5, delay: i * 0.05 }} className="w-full max-w-[32px] rounded-t" style={{ backgroundColor: color, minHeight: 4 }} />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-[9px] text-[var(--text-muted)]">Oldest</span>
                  <span className="text-[9px] text-[var(--text-muted)]">Latest</span>
                </div>
              </div>
            )}

            <div className="space-y-4" data-testid="timeline-entries">
              {timeline.map((entry, idx) => {
                const prev = idx < timeline.length - 1 ? timeline[idx + 1] : null;
                const isCurrent = entry._label === "Current";
                return (
                  <motion.div key={entry._label || entry.extracted_at || idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}
                    className={`bg-[var(--bg-card)] border rounded-lg p-5 space-y-4 ${isCurrent ? "border-[#FF4400]/30" : "border-[var(--border)]"}`} data-testid={`timeline-entry-${idx}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {isCurrent && <span className="px-2 py-0.5 text-[10px] font-medium rounded bg-[#FF4400]/10 text-[#FF4400] border border-[#FF4400]/20">CURRENT</span>}
                        <span className="text-xs font-medium text-[var(--text-secondary)]">{entry._label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <ConfidenceDot score={entry.confidence_score || 0} />
                        <span className="text-xs font-mono text-[var(--text-muted)]">{entry.confidence_score}/10</span>
                        {prev && <TrendArrow prev={prev.confidence_score || 0} curr={entry.confidence_score || 0} />}
                      </div>
                    </div>
                    <p className="text-[10px] text-[var(--text-muted)] flex items-center gap-1"><Clock size={10} /> {formatDate(entry.extracted_at)}</p>
                    <div className="bg-[var(--bg-base)] border border-[var(--border)] rounded-lg p-3">
                      <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider block mb-1">Voice Summary</span>
                      <p className="text-sm text-[var(--text-secondary)] leading-relaxed italic">"{entry.voice_summary}"</p>
                    </div>
                    {prev && (
                      <div className="space-y-3 pt-2 border-t border-[var(--border)]">
                        <span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Changes from previous</span>
                        <DiffChips label="Tone Attributes" prev={prev.tone_attributes} curr={entry.tone_attributes} />
                        <DiffChips label="Personality Traits" prev={prev.personality_traits} curr={entry.personality_traits} />
                        <DiffChips label="What to Avoid" prev={prev.what_to_avoid} curr={entry.what_to_avoid} />
                      </div>
                    )}
                    <details className="group">
                      <summary className="text-[10px] text-[var(--text-muted)] cursor-pointer hover:text-[var(--text-primary)] transition-colors select-none">View full profile details</summary>
                      <div className="mt-3 space-y-3 text-xs text-[var(--text-secondary)]">
                        <div><span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider block mb-1">Vocabulary Style</span><p>{entry.vocabulary_style || "—"}</p></div>
                        <div><span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider block mb-1">Sentence Structure</span><p>{entry.sentence_structure || "—"}</p></div>
                        {(entry.sample_phrases || []).length > 0 && (
                          <div><span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider block mb-1">Sample Phrases</span>
                            <div className="space-y-1">{entry.sample_phrases.map((p, i) => <p key={`phrase-${p.slice(0,20)}-${i}`} className="italic text-[var(--text-secondary)]">"{p}"</p>)}</div>
                          </div>
                        )}
                        <div><span className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider block mb-1">Examples Used</span><p>{(entry.source_examples || []).length} example(s) provided</p></div>
                      </div>
                    </details>
                  </motion.div>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
